import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, BookOpen, Brush, ArrowLeft } from "lucide-react";
import StudioShell from "../components/studio/StudioShell";
import RangoliActivity from "../components/studio/RangoliActivity";
import { useStamp, StampToast } from "../components/studio/Passport";
import { RANGOLI_FACTS, RANGOLI_STAMPS } from "../data/rangoli";

const STEPS = [
  { id: "learn", label: "1. Learn", icon: BookOpen },
  { id: "build", label: "2. Create", icon: Brush },
];

// Quick mini-rangoli for the Learn page sample showcase.
function SampleRangoli({ symmetry = 8, accent = "#E54B6A", secondary = "#F5B400" }) {
  const C = 100;
  const rings = [
    { stamp: "dot", color: accent, r: 0, size: 0.8 },
    { stamp: "petal", color: secondary, r: 22, size: 0.9 },
    { stamp: "diamond", color: accent, r: 50, size: 0.8 },
    { stamp: "leaf", color: secondary, r: 70, size: 0.8 },
  ];
  return (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
      <rect width="100%" height="100%" fill="#F9F6F0" />
      {rings.flatMap((ring) => {
        const stamp = RANGOLI_STAMPS.find((s) => s.id === ring.stamp);
        if (!stamp) return [];
        if (ring.r === 0) {
          return [
            <g
              key={`center-${ring.stamp}`}
              transform={`translate(${C},${C}) scale(${ring.size})`}
              color={ring.color}
            >
              {stamp.svg}
            </g>,
          ];
        }
        return Array.from({ length: symmetry }).map((_, i) => {
          const a = (Math.PI * 2 * i) / symmetry;
          const x = C + ring.r * Math.cos(a);
          const y = C + ring.r * Math.sin(a);
          const rot = (360 * i) / symmetry;
          return (
            <g
              key={`${ring.stamp}-${i}`}
              transform={`translate(${x},${y}) rotate(${rot}) scale(${ring.size})`}
              color={ring.color}
            >
              {stamp.svg}
            </g>
          );
        });
      })}
    </svg>
  );
}

export default function RangoliStudio() {
  const [step, setStep] = useState("learn");
  const { earn, showToast, dismiss } = useStamp("rangoli");

  return (
    <StudioShell>
      {/* Hero */}
      <section className="relative overflow-hidden pt-12 pb-8 sm:pt-16 sm:pb-12">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 relative text-center">
          <span className="inline-flex items-center gap-2 text-[#E54B6A]">
            <Sparkles size={18} />
            <span className="overline !text-[#E54B6A]">Module 3 · All of India</span>
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
            Create a
            <br />
            <em className="italic font-light">festival rangoli.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
            Tap anywhere on the canvas and your stamp blooms in beautiful symmetry around the
            centre — just like a real Diwali rangoli.
          </p>
        </div>
      </section>

      <nav className="max-w-6xl mx-auto px-4 sm:px-10" data-testid="rangoli-steps">
        <div className="grid grid-cols-2 gap-2 sm:gap-4 p-2 sm:p-3 rounded-full bg-white border border-[#E5DFD3] shadow-sm">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const active = step === s.id;
            return (
              <button
                key={s.id}
                data-testid={`rangoli-step-${s.id}`}
                onClick={() => setStep(s.id)}
                className={`flex items-center justify-center gap-2 px-3 sm:px-5 py-3 rounded-full text-xs sm:text-sm tracking-wide transition-all duration-500 ${
                  active ? "bg-[#E54B6A] text-[#F9F6F0]" : "text-[#5C6A8F] hover:text-[#1E2A4F]"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.label.split(". ")[1]}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-10 sm:py-16 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
          >
            {step === "learn" && <RangoliLearn />}
            {step === "build" && (
              <>
                <div className="text-center mb-10">
                  <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
                    Make your rangoli.
                  </h2>
                  <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
                    Pick a stamp, a colour, and a symmetry. Tap the canvas to bloom petals
                    around the centre. Use undo to fix mistakes, save when you&apos;re happy.
                  </p>
                </div>
                <RangoliActivity onSave={earn} />
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-3">
          {step === "build" && (
            <button
              data-testid="rangoli-prev"
              onClick={() => setStep("learn")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
            >
              <ArrowLeft size={14} /> Previous
            </button>
          )}
          {step === "learn" && (
            <button
              data-testid="rangoli-next"
              onClick={() => setStep("build")}
              className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E54B6A] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
            >
              Start creating →
            </button>
          )}
        </div>
      </section>

      <StampToast show={showToast} artName="Rangoli" onDismiss={dismiss} />
    </StudioShell>
  );
}

function RangoliLearn() {
  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
          The story of rangoli.
        </h2>
        <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
          Coloured powder, fingertips, flower petals — and a quiet patience for symmetry.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-10">
        {RANGOLI_FACTS.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`rangoli-fact-${i}`}
            className="p-6 sm:p-7 rounded-2xl bg-white border border-[#E5DFD3] hover:border-[#E54B6A] transition-colors duration-500"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FFE5EE] text-2xl">
              {f.emoji}
            </span>
            <h3 className="font-display text-2xl text-[#1E2A4F] mt-4">{f.title}</h3>
            <p className="text-[15px] text-[#5C6A8F] leading-relaxed mt-2">{f.body}</p>
          </motion.div>
        ))}
      </div>

      {/* Sample showcase */}
      <div
        className="p-6 sm:p-10 rounded-2xl bg-[#FFE5EE]"
        data-testid="rangoli-sample"
      >
        <p className="overline !text-[#E54B6A] text-center">A glimpse of the art</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-[#E5DFD3] p-3">
            <SampleRangoli symmetry={8} accent="#E54B6A" secondary="#F5B400" />
            <p className="text-center text-xs tracking-[0.2em] uppercase text-[#5C6A8F] mt-2">
              8-fold
            </p>
          </div>
          <div className="bg-white rounded-xl border border-[#E5DFD3] p-3">
            <SampleRangoli symmetry={6} accent="#1F76A8" secondary="#C03546" />
            <p className="text-center text-xs tracking-[0.2em] uppercase text-[#5C6A8F] mt-2">
              6-fold
            </p>
          </div>
          <div className="bg-white rounded-xl border border-[#E5DFD3] p-3">
            <SampleRangoli symmetry={12} accent="#6A4FB6" secondary="#3F8E4D" />
            <p className="text-center text-xs tracking-[0.2em] uppercase text-[#5C6A8F] mt-2">
              12-fold
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#1E2A4F] text-[#F9F6F0] text-center">
        <p className="overline !text-[#E76F51]">A note from Meera</p>
        <p className="font-display italic text-xl sm:text-2xl mt-3 leading-snug max-w-2xl mx-auto">
          &ldquo;The magic of rangoli is that the more symmetry you use, the more your one little
          touch grows into something big and beautiful.&rdquo;
        </p>
      </div>
    </div>
  );
}
